<script setup lang="ts">
  /**
   * Forgot Password View
   *
   * Request password reset email.
   */
  import { ref } from 'vue'
  import { authService } from '../../services/auth.service'
  import { useNotifications } from '../../composables/useNotifications'

  const { showSuccess, showError } = useNotifications()

  const email = ref('')
  const loading = ref(false)
  const submitted = ref(false)

  async function handleSubmit() {
    if (!email.value) {
      showError('Error', 'Please enter your email address')
      return
    }

    loading.value = true

    try {
      await authService.forgotPassword({ email: email.value })
      submitted.value = true
      showSuccess('Email Sent', 'Check your inbox for password reset instructions')
    } catch (error) {
      // Don't reveal if email exists or not for security
      submitted.value = true
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div class="flex min-h-screen items-center justify-center bg-secondary-50 px-4">
    <div class="w-full max-w-md">
      <div class="rounded-xl bg-white p-8 shadow-lg">
        <!-- Header -->
        <div class="mb-8 text-center">
          <h1 class="text-2xl font-bold text-secondary-900">Reset Password</h1>
          <p class="mt-2 text-secondary-600">
            Enter your email and we'll send you instructions to reset your password.
          </p>
        </div>

        <!-- Success state -->
        <template v-if="submitted">
          <div class="text-center">
            <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-50">
              <i class="pi pi-check text-3xl text-success-600" />
            </div>
            <h2 class="text-lg font-semibold text-secondary-900">Check your email</h2>
            <p class="mt-2 text-secondary-600">
              If an account exists for {{ email }}, you'll receive password reset instructions.
            </p>
            <RouterLink
              to="/login"
              class="mt-6 inline-flex items-center text-primary-600 hover:text-primary-700"
            >
              <i class="pi pi-arrow-left mr-2" />
              Back to login
            </RouterLink>
          </div>
        </template>

        <!-- Form -->
        <template v-else>
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div>
              <label for="email" class="mb-2 block text-sm font-medium text-secondary-700">
                Email Address
              </label>
              <PInputText
                id="email"
                v-model="email"
                type="email"
                placeholder="you@company.com"
                class="w-full"
                :disabled="loading"
                autocomplete="email"
              />
            </div>

            <PButton
              type="submit"
              label="Send Reset Link"
              icon="pi pi-send"
              :loading="loading"
              class="w-full"
            />
          </form>

          <div class="mt-6 text-center">
            <RouterLink
              to="/login"
              class="text-sm text-secondary-600 hover:text-secondary-900"
            >
              <i class="pi pi-arrow-left mr-1" />
              Back to login
            </RouterLink>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
